import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Dialog, IconButton, Spinner, useToast } from '@economic/taco';
import { Trans } from '@lingui/macro';
import { ProgressDots } from '~/app/shared/components/ui/progressDots/ProgressDots';
import { t, useLocalization } from '../../../../shared/components/l10n/l10n';
import Introduction from './Introduction';
import internalApiClient from '../../../../shared/util/api/internalApiClient';
import { OnboardingStep } from './types';
import ContactMeHelp from './ContactMe';
import ContactForm from './ContactForm';
import OnBoardingCompleted from './OnBoardingCompleted';
import VismaEconomicLogo from './images/visma-economic-logo.svg';
import RackbeatLogo from './images/rackbeat-logo.svg';

function RackbeatLagerOnboardingDialog() {
    const location = useLocation();
    const loadingTexts = useLocalization(textIds);
    const navigate = useNavigate();
    const open = location.hash === '#onboardRackbeatLager';
    const toast = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState<OnboardingStep | null>(OnboardingStep.Introduction);

    const completeOnBoarding = async () => {
        try {
            setIsLoading(true);
            const response = await internalApiClient.post('rackbeat-lager/company/create-grant');

            if (response.data) {
                toast.success(t`RACKBEAT_LAGER_ONBOARDING_INSTALL_SUCCESS`);
                window.open(response.data, '_blank');
                setStep(OnboardingStep.OnBoardingCompleted);
            } else {
                toast.error(t`RACKBEAT_LAGER_ONBOARDING_INSTALL_ERROR`);
            }
        } catch (error) {
            toast.error(t`RACKBEAT_LAGER_ONBOARDING_INSTALL_ERROR`);
        } finally {
            setIsLoading(false);
        }
    };

    const onContactMeClick = () => {
        setStep(OnboardingStep.ContactMeHelp);
        window.open('https://www.e-conomic.dk/funktioner/lager-af-rackbeat#kontakt', '_blank');
    };

    const onCompletedClick = () => {
        navigate('/market/addon/rackbeatlager');
        window.location.reload();
    };

    const refreshPage = () => {
        navigate(location.pathname);
    };

    if (loadingTexts || isLoading) {
        return (
            <Dialog open={open} onClose={() => refreshPage()}>
                <Dialog.Content className="flex flex-col items-center" aria-label={t`TOAST_LOADING`}>
                    <Spinner />
                </Dialog.Content>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={() => refreshPage()} closeOnEscape={false} size="md">
            <Dialog.Content aria-label="rackbeatlager" className="pt-6 pr-6">
                {step !== OnboardingStep.Introduction && step !== OnboardingStep.OnBoardingCompleted && (
                    <IconButton
                        icon="arrow-left"
                        appearance="discrete"
                        className="-ml-2 -mt-5"
                        onClick={() => setStep(OnboardingStep.Introduction)}
                    />
                )}
                {step === OnboardingStep.Introduction && (
                    <Introduction>
                        <ProgressDots stepsCount={4} currentStep={1} />
                    </Introduction>
                )}
                {step === OnboardingStep.ContactForm && (
                    <ContactForm>
                        <ProgressDots stepsCount={4} currentStep={2} />
                    </ContactForm>
                )}
                {step === OnboardingStep.ContactMeHelp && (
                    <ContactMeHelp>
                        <ProgressDots stepsCount={4} currentStep={3} />
                    </ContactMeHelp>
                )}
                {step === OnboardingStep.OnBoardingCompleted && (
                    <OnBoardingCompleted>
                        <ProgressDots stepsCount={4} currentStep={4} />
                    </OnBoardingCompleted>
                )}
                <div className="flex justify-end">
                    {step === OnboardingStep.Introduction && (
                        <div className="space-x-4">
                            <Button appearance="default" onClick={() => onContactMeClick()} autoFocus>
                                <Trans>Contact me</Trans>
                            </Button>
                            <Button appearance="primary" onClick={() => setStep(OnboardingStep.ContactForm)} autoFocus>
                                <Trans>Continue</Trans>
                            </Button>
                        </div>
                    )}
                    {step === OnboardingStep.ContactMeHelp && (
                        <Button appearance="primary" onClick={() => setStep(OnboardingStep.Introduction)} autoFocus>
                            <Trans>Ok</Trans>
                        </Button>
                    )}
                    {step === OnboardingStep.ContactForm && (
                        <Button appearance="primary" onClick={() => completeOnBoarding()} autoFocus>
                            <Trans>Go to Rackbeat to complete onboarding</Trans>
                        </Button>
                    )}
                    {step === OnboardingStep.OnBoardingCompleted && (
                        <Button appearance="primary" onClick={() => onCompletedClick()} autoFocus>
                            <Trans>Ok</Trans>
                        </Button>
                    )}
                </div>
                <Dialog.Footer className="flex justify-between bg-grey-100 -mx-6 -mb-6 align-middle pt-2 pb-4">
                    <div className="flex text-end ml-6 pt-2">
                        <img src={VismaEconomicLogo} className="h-[24px]" />
                    </div>
                    <div className="flex text-end mr-6 pt-2">
                        <div className="text-xs mr-4 mt-1">
                            <Trans>Provided by</Trans>
                        </div>
                        <div>
                            <img src={RackbeatLogo} className="h-[24px]" />
                        </div>
                    </div>
                </Dialog.Footer>
            </Dialog.Content>
        </Dialog>
    );
}

const textIds = [
    'TOAST_LOADING',
    'RACKBEAT_LAGER_ONBOARDING_CONFIRM_HEADER',
    'RACKBEAT_LAGER_ONBOARDING_CONFIRM_TEXT1',
    'RACKBEAT_LAGER_ONBOARDING_CONFIRM_TEXT2',
    'RACKBEAT_LAGER_ONBOARDING_CONFIRM_ACCEPT_BUTTON',
    'RACKBEAT_LAGER_ONBOARDING_CONFIRM_CANCEL_BUTTON',
    'RACKBEAT_LAGER_ONBOARDING_INSTALL_SUCCESS',
    'RACKBEAT_LAGER_ONBOARDING_INSTALL_ERROR',
    'RACKBEAT_LAGER_MARKET_ADDON_REMOVE_WARNING_TEXT',
];

export default RackbeatLagerOnboardingDialog;
